import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, File, Trash2, AlertTriangle, CheckCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { apiRequest } from '@/lib/queryClient';

interface FileItem {
  id: string;
  name: string;
  type: string;
  path: string;
  size: number;
  lastUpdated: string;
  updatedBy: string;
  reviewStatus: string;
  originator: string;
  revision: string;
  accUrl: string;
  versionId?: string;
  currentVersionId?: string;
  version_label?: string;
  revision_attribute?: string;
  isOutdated?: boolean;
  added_by?: string;
  created_by?: string;
  version?: string;
  latest_version?: string;
}

interface FolderNode {
  name: string;
  path: string;
  children: FolderNode[];
  files: FileItem[];
}

interface FolderTreeViewProps {
  items: FileItem[];
  onRemoveItem?: (itemId: string) => void;
  packageId?: string;
  selectedItems?: string[];
  onSelectItem?: (itemId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  isLocked?: boolean;
  hideStatusColumn?: boolean;
  onDataRefresh?: () => void; // Callback to refresh data after version check
}

export function FolderTreeView({ 
  items, 
  onRemoveItem, 
  packageId, 
  selectedItems = [], 
  onSelectItem, 
  onSelectAll,
  isLocked = false,
  hideStatusColumn = false,
  onDataRefresh
}: FolderTreeViewProps) {
  const [folderStates, setFolderStates] = useState<Record<string, boolean>>({});
  const [versionStates, setVersionStates] = useState<Record<string, 'checking' | 'current' | 'outdated' | 'error'>>({});
  const [isAutoChecking, setIsAutoChecking] = useState(false);
  const [revisionLabels, setRevisionLabels] = useState<Record<string, string>>({});
  const [isLoadingRevisionLabels, setIsLoadingRevisionLabels] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [addedByFilter, setAddedByFilter] = useState('');

  // Filter items based on search and added by filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchFilter || 
        item.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        item.path.toLowerCase().includes(searchFilter.toLowerCase());
      
      const matchesAddedBy = !addedByFilter || 
        (item.added_by && item.added_by.toLowerCase().includes(addedByFilter.toLowerCase()));
      
      return matchesSearch && matchesAddedBy;
    });
  }, [items, searchFilter, addedByFilter]);

  // Get unique added by values for filter dropdown
  const uniqueAddedByValues = useMemo(() => {
    const values = new Set<string>();
    items.forEach(item => {
      if (item.added_by) {
        values.add(item.added_by);
      }
    });
    return Array.from(values).sort();
  }, [items]);

  // Initialize version states from the isOutdated flag in the data
  useEffect(() => {
    const initialStates: Record<string, 'current' | 'outdated'> = {};
    filteredItems.forEach(item => {
      if (item.isOutdated) {
        initialStates[item.id] = 'outdated';
      } else {
        initialStates[item.id] = 'current';
      }
    });
    setVersionStates(initialStates);
  }, [filteredItems]);

  // Load revision labels from API
  const loadRevisionLabels = async () => {
    if (!packageId || isLoadingRevisionLabels) {
      return;
    }
    
    setIsLoadingRevisionLabels(true);
    try {
      const url = `/api/tender-packages/${packageId}/revision-labels?t=${Date.now()}`;
      const response = await apiRequest(url);
      
      if (response && response.revisionLabels) {
        setRevisionLabels(response.revisionLabels);
      }
    } catch (error) {
      console.error('Error loading revision labels:', error);
    } finally {
      setIsLoadingRevisionLabels(false);
    }
  };

  // Load revision labels when component mounts or packageId changes
  useEffect(() => {
    if (packageId && items.length > 0) {
      loadRevisionLabels();
    }
  }, [packageId, items.length]);

  const extractVersionFromUrl = (accUrl: string, versionId?: string, itemId?: string, revisionLabel?: string): string => {
    // First try to use database revision label (most reliable)
    if (revisionLabel) {
      const revision = revisionLabel.replace('V', ''); // Remove 'V' prefix to match existing logic
      return revision;
    }
    
    // Second try to get revision label from API response
    if (itemId && revisionLabels[itemId]) {
      const revision = revisionLabels[itemId].replace('V', ''); // Remove 'V' prefix to match existing logic
      return revision;
    }
    
    // Fallback to original logic
    if (versionId) {
      // Extract version number from versionId (format like "urn:adsk.wipprod:fs.file:vf.xyz?version=2")
      const versionMatch = versionId.match(/version=(\d+)/);
      if (versionMatch) {
        return versionMatch[1];
      }
      // If no version pattern found, maybe it's just a version ID - try parsing numbers
      const directNumber = versionId.match(/(\d+)$/);
      if (directNumber) {
        return directNumber[1];
      }
    }
    
    // Fallback to accUrl
    if (accUrl) {
      const versionMatch = accUrl.match(/version=(\d+)/);
      if (versionMatch) {
        return versionMatch[1];
      }
    }
    
    return 'Unknown';
  };

  const checkAllVersions = async () => {
    if (!packageId) {
      console.error('[AUTO-VERSION-CHECK] Package ID is undefined');
      return;
    }

    // Set all files to checking state
    const checkingStates: Record<string, 'checking'> = {};
    items.forEach(item => {
      checkingStates[item.id] = 'checking';
    });
    setVersionStates(checkingStates);

    try {
      console.log(`[AUTO-VERSION-CHECK] Checking versions for package ${packageId}`);
      const response: any = await apiRequest(`/api/tender-packages/${packageId}/check-updates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const updatedStates: Record<string, 'current' | 'outdated' | 'error'> = {};
      
      console.log(`[AUTO-VERSION-CHECK] Response:`, response);
      
      // Process response to update version states
      if (response.outdatedFiles && Array.isArray(response.outdatedFiles)) {
        const outdatedIds = new Set(response.outdatedFiles.map((file: any) => file.id));
        console.log(`[AUTO-VERSION-CHECK] Found ${response.outdatedFiles.length} outdated files:`, response.outdatedFiles.map(f => f.name));
        
        items.forEach(item => {
          updatedStates[item.id] = outdatedIds.has(item.id) ? 'outdated' : 'current';
        });
      } else {
        // Default to current if no outdated files returned
        items.forEach(item => {
          updatedStates[item.id] = 'current';
        });
      }
      
      setVersionStates(updatedStates);
      setIsAutoChecking(false);
      
      // Refresh data after version check to get updated version labels
      if (onDataRefresh) {
        console.log('[AUTO-VERSION-CHECK] Refreshing data after version check');
        onDataRefresh();
      }
    } catch (error) {
      console.error('Error checking versions:', error);
      // Set all to error state
      const errorStates: Record<string, 'error'> = {};
      items.forEach(item => {
        errorStates[item.id] = 'error';
      });
      setVersionStates(errorStates);
      setIsAutoChecking(false);
    }
  };

  const updateSelectedToLatest = async () => {
    if (selectedItems.length === 0 || !packageId) return;
    
    try {
      console.log(`[UPDATE-SELECTED] Updating ${selectedItems.length} selected files to latest version`);
      const response = await apiRequest(`/api/tender-packages/${packageId}/auto-update`, {
        method: 'POST',
        body: JSON.stringify({ updatedBy: 'User' })
      });
      
      console.log(`[UPDATE-SELECTED] Update response:`, response);
      
      // Refresh the revision labels after update
      await loadRevisionLabels();
      
      // Update version states to current
      const updatedStates: Record<string, 'current'> = {};
      selectedItems.forEach(itemId => {
        updatedStates[itemId] = 'current';
      });
      setVersionStates(prev => ({ ...prev, ...updatedStates }));
      
      // Refresh data after update to get updated version labels
      if (onDataRefresh) {
        console.log('[UPDATE-SELECTED] Refreshing data after update');
        onDataRefresh();
      }
      
    } catch (error) {
      console.error('Error updating selected files:', error);
    }
  };

  // Build folder tree structure from flat file list
  const buildFolderTree = (items: FileItem[]): FolderNode[] => {
    const root: FolderNode[] = [];
    const folderMap = new Map<string, FolderNode>();

    // Create folder hierarchy
    items.forEach(item => {
      const pathParts = item.path.split('/').filter(part => part.length > 0);
      
      // Handle root level files (path is "/" or empty)
      if (pathParts.length === 0) {
        // Create a root folder for files at the root level
        let rootFolder = folderMap.get('/');
        if (!rootFolder) {
          rootFolder = {
            name: 'Root',
            path: '/',
            children: [],
            files: []
          };
          folderMap.set('/', rootFolder);
          root.push(rootFolder);
        }
        rootFolder.files.push(item);
        return;
      }
      
      let currentPath = '';
      let currentLevel = root;

      pathParts.forEach((part, index) => {
        const parentPath = currentPath;
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        
        let folder = folderMap.get(currentPath);
        if (!folder) {
          folder = {
            name: part,
            path: currentPath,
            children: [],
            files: []
          };
          folderMap.set(currentPath, folder);
          currentLevel.push(folder);
        }
        
        // Update currentLevel for next iteration (always point to this folder's children)
        if (index < pathParts.length - 1) {
          // This is not the last part, so definitely a folder - move to its children level
          currentLevel = folder.children;
        } else {
          // This is the last part
          if (item.type === 'file') {
            // This is a file, add it to the current folder
            folder.files.push(item);
          } else {
            // This is a folder item itself - it's already been added as a node above
            // Just ensure currentLevel is set correctly for potential children
            currentLevel = folder.children;
          }
        }
      });
    });

    // Sort folders and files
    const sortFolders = (nodes: FolderNode[]) => {
      nodes.sort((a, b) => {
        const smartCompare = (a: string, b: string) => {
          // Try to extract numeric prefixes like "1. Introduction", "2. Details"
          const aNumMatch = a.match(/^(\d+)\.\s*(.+)/);
          const bNumMatch = b.match(/^(\d+)\.\s*(.+)/);
          
          if (aNumMatch && bNumMatch) {
            // Both have numeric prefixes, compare numerically
            const aNum = parseInt(aNumMatch[1]);
            const bNum = parseInt(bNumMatch[1]);
            return aNum - bNum;
          }
          
          // Try to extract letter patterns like "A. Overview", "B. Details"
          const aLetterMatch = a.match(/^([A-Z])\.\s*(.+)/);
          const bLetterMatch = b.match(/^([A-Z])\.\s*(.+)/);
          
          if (aLetterMatch && bLetterMatch) {
            // Both have letter prefixes, compare alphabetically
            return aLetterMatch[1].localeCompare(bLetterMatch[1]);
          }
          
          // Fall back to localeCompare for other cases
          return a.localeCompare(b, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
        };
        
        return smartCompare(a.name, b.name);
      });
      
      // Recursively sort children
      nodes.forEach(node => {
        if (node.children.length > 0) {
          sortFolders(node.children);
        }
      });
    };
    
    // Apply sorting to the root level
    sortFolders(root);

    return root;
  };

  const toggleFolder = (path: string) => {
    setFolderStates(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getVersionStatus = (fileId: string) => {
    const status = versionStates[fileId] || 'checking';
    switch (status) {
      case 'checking':
        return { icon: Clock, text: 'Checking...', className: 'text-blue-600' };
      case 'current':
        return { icon: CheckCircle, text: 'Current', className: 'text-green-600' };
      case 'outdated':
        return { icon: AlertTriangle, text: 'Outdated', className: 'text-orange-600' };
      case 'error':
        return { icon: AlertTriangle, text: 'Error', className: 'text-red-600' };
      default:
        return { icon: Clock, text: 'Unknown', className: 'text-gray-600' };
    }
  };

  // Helper function to get all file IDs within a folder (recursively)
  const getAllFolderFileIds = (node: FolderNode): string[] => {
    const fileIds: string[] = [];
    
    // Add files in this folder
    fileIds.push(...node.files.map(file => file.id));
    
    // Recursively add files from subfolders
    node.children.forEach(child => {
      fileIds.push(...getAllFolderFileIds(child));
    });
    
    return fileIds;
  };

  // Helper function to get total file count within a folder (recursively)
  const getTotalFileCount = (node: FolderNode): number => {
    let count = node.files.length;
    
    // Recursively count files from subfolders
    node.children.forEach(child => {
      count += getTotalFileCount(child);
    });
    
    return count;
  };

  const renderFolderNode = (node: FolderNode, level: number = 0): React.ReactNode => {
    const isExpanded = folderStates[node.path] ?? true;
    const hasContent = node.children.length > 0 || node.files.length > 0;

    return (
      <div key={node.path}>
        {/* Folder Header - aligned with file structure */}
        <div 
          className="flex items-center py-1 px-2 hover:bg-gray-50"
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          {/* Folder Selection Checkbox */}
          {onSelectItem && !isLocked && (
            <div className="w-10 flex items-start justify-start pl-2 pt-1" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={getAllFolderFileIds(node).every(id => selectedItems.includes(id))}
                onCheckedChange={(checked) => {
                  const allFileIds = getAllFolderFileIds(node);
                  allFileIds.forEach(fileId => {
                    onSelectItem(fileId, checked as boolean);
                  });
                }}
              />
            </div>
          )}
          
          {/* Folder name section - matching file layout */}
          <div className="flex items-center cursor-pointer flex-1 pl-2" onClick={() => toggleFolder(node.path)}>
            {hasContent && (
              isExpanded ? 
                <ChevronDown className="h-4 w-4 mr-1 text-gray-400" /> : 
                <ChevronRight className="h-4 w-4 mr-1 text-gray-400" />
            )}
            {isExpanded ? 
              <FolderOpen className="h-4 w-4 mr-2 text-blue-500" /> : 
              <Folder className="h-4 w-4 mr-2 text-blue-500" />
            }
            <span className="text-sm font-medium text-gray-700 select-text">{node.name}</span>
          </div>
          
          {/* Empty spaces to align with file attributes columns */}
          <div className="w-20"></div> {/* File Size column */}
          <div className="w-16"></div> {/* Version column */}
          <div className="w-24"></div> {/* Created By column */}
          <div className="w-24"></div> {/* Added By column */}
          {!hideStatusColumn && (
            <div className="w-20 text-xs text-gray-400 text-center">
              ({getTotalFileCount(node)} files)
            </div>
          )} {/* Status column - show file count (recursive) */}
          <div className="w-10"></div> {/* Actions column */}
        </div>

        {/* Folder Contents */}
        {isExpanded && (
          <div>
            {/* Child Folders */}
            {node.children.map(childNode => renderFolderNode(childNode, level + 1))}
            
            {/* Files */}
            {node.files.map(file => {
              const versionStatus = getVersionStatus(file.id);
              const StatusIcon = versionStatus.icon;
              
              return (
                <div 
                  key={file.id} 
                  className="flex items-center py-2 px-2 hover:bg-gray-50 border-l border-gray-200"
                  style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
                >
                  {onSelectItem && (
                    <div className="w-10 flex items-start justify-start pl-2 pt-1">
                      <Checkbox
                        checked={selectedItems.includes(file.id)}
                        onCheckedChange={(checked) => onSelectItem(file.id, checked as boolean)}
                      />
                    </div>
                  )}
                  
                  {/* File name - matching folder structure width */}
                  <div className="flex items-center flex-1 min-w-0 select-text pl-2">
                    <File className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{file.name}</span>
                  </div>
                  
                  {/* File size - fixed width to align with header */}
                  <div className="w-20 text-xs text-gray-500 text-center select-text">
                    {file.size ? formatFileSize(file.size) : '-'}
                  </div>
                  
                  {/* Version - fixed width to align with header */}
                  <div className="w-16 text-xs text-gray-500 text-center select-text">
                    {(() => {
                      // 优先使用version_label，确保加上V前缀
                      if (file.version_label) {
                        const versionStr = file.version_label.toString();
                        return versionStr.startsWith('V') ? versionStr : `V${versionStr}`;
                      }
                      // 然后使用version字段
                      if (file.version) {
                        const versionStr = file.version.toString();
                        return versionStr.startsWith('V') ? versionStr : `V${versionStr}`;
                      }
                      // 最后使用fallback逻辑
                      const version = extractVersionFromUrl(file.accUrl, file.versionId, file.id, file.revisionLabel);
                      return `V${version}`;
                    })()}
                  </div>
                  
                  {/* Created by - fixed width to align with header */}
                  <div className="w-24 text-xs text-gray-500 text-center truncate select-text">
                    {file.created_by || file.updatedBy}
                  </div>
                  
                  {/* Added by - fixed width to align with header */}
                  <div className="w-24 text-xs text-gray-500 text-center truncate select-text">
                    {file.added_by || '-'}
                  </div>
                  
                  {/* Status - fixed width to align with header */}
                  {!hideStatusColumn && (
                    <div className={`w-20 flex items-center justify-center space-x-1 text-xs ${versionStatus.className}`}>
                      <StatusIcon className="h-3 w-3" />
                      <span>{versionStatus.text}</span>
                    </div>
                  )}
                  
                  {/* Actions - fixed width to align with header */}
                  <div className="w-10 flex justify-center">
                    {onRemoveItem && !isLocked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem(file.id);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const folderTree = buildFolderTree(filteredItems);

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Folder className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>No files added to this package yet</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <div className="bg-gray-50 px-4 py-2 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Package Contents</h3>
            <p className="text-xs text-gray-500 mt-1">
              Files are organized by their folder structure in Autodesk Construction Cloud
            </p>
          </div>
          {(isAutoChecking || Object.values(versionStates).some(state => state === 'checking')) && (
            <div className="flex items-center space-x-2 text-xs text-blue-600">
              <RefreshCw className="h-3 h-3 animate-spin" />
              <span>Checking versions...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search files..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="w-48">
            <select
              value={addedByFilter}
              onChange={(e) => setAddedByFilter(e.target.value)}
              className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Added By</option>
              {uniqueAddedByValues.map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
          {(searchFilter || addedByFilter) && (
            <button
              onClick={() => {
                setSearchFilter('');
                setAddedByFilter('');
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Column Headers */}
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <div className="flex items-center">
          {onSelectItem && (
            <div className="w-10 flex items-start justify-start pl-2 pt-1">
              <Checkbox
                checked={items.length > 0 && items.every(item => selectedItems.includes(item.id))}
                onCheckedChange={(checked) => onSelectAll?.(checked as boolean)}
              />
            </div>
          )}
          <div className="flex-1 text-xs font-medium text-gray-600 uppercase tracking-wide">Folder/File Name</div>
          <div className="w-20 text-xs font-medium text-gray-600 uppercase tracking-wide text-center">File Size</div>
          <div className="w-16 text-xs font-medium text-gray-600 uppercase tracking-wide text-center">Version</div>
          <div className="w-24 text-xs font-medium text-gray-600 uppercase tracking-wide text-center">Created By</div>
          <div className="w-24 text-xs font-medium text-gray-600 uppercase tracking-wide text-center">Added By</div>
          {!hideStatusColumn && (
            <div className="w-20 text-xs font-medium text-gray-600 uppercase tracking-wide text-center">Status</div>
          )}
          <div className="w-10"></div> {/* Space for remove button */}
        </div>
      </div>
      
      {/* Update to Latest Button - shown when files are selected */}
      {selectedItems.length > 0 && packageId && (
        <div className="bg-blue-50 border-b border-blue-200 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {selectedItems.length} file{selectedItems.length > 1 ? 's' : ''} selected
            </span>
            <Button
              onClick={() => updateSelectedToLatest()}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Update Selected to Latest
            </Button>
          </div>
        </div>
      )}
      
      <div className="max-h-96 overflow-y-auto">
        {folderTree.map(node => renderFolderNode(node))}
      </div>
    </div>
  );
}
